import * as React from 'react';
import clsx from 'clsx';
import {
  VideoCameraIcon,
  DocumentTextIcon,
  MusicalNoteIcon,
  XMarkIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import type { UploadedFile } from '@/types/echo_creation';

const isDocumentType = (mimeType: string) =>
  mimeType === 'application/pdf' || mimeType.includes('document') || mimeType.includes('docx');

interface FileListItemProps {
  file: UploadedFile;
  onCancel: (id: string) => void;
  onRetry: (id: string) => void;
  onDelete: (id: string) => void;
  onToggleScreenshots?: (id: string, value: boolean) => void;
}

export const FileListItem: React.FC<FileListItemProps> = ({
  file,
  onCancel,
  onRetry,
  onDelete,
  onToggleScreenshots,
}) => {
  // Format bytes to GB/MB
  const formatSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const units = ['B', 'KB', 'MB', 'GB'];
    const idx = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), units.length - 1);
    const value = bytes / Math.pow(k, idx);
    const unit = units[idx] ?? 'B';
    return `${parseFloat(value.toFixed(2))} ${unit.toLowerCase()}`;
  };

  const getIcon = () => {
    if (file.type.startsWith('video/')) return <VideoCameraIcon className='size-6 text-white' />;
    if (file.type.startsWith('audio/')) return <MusicalNoteIcon className='size-6 text-white' />;
    return <DocumentTextIcon className='size-6 text-white' />;
  };

  const showScreenshotToggle =
    file.status === 'completed' && isDocumentType(file.type) && onToggleScreenshots;

  return (
    <div
      className={clsx(
        'flex w-full items-center',
        'gap-4 rounded-20 bg-white/60 p-3 pr-4 backdrop-blur-sm',
        'transition-all duration-200',
        showScreenshotToggle ? 'min-h-[71px]' : 'h-[71px]'
      )}
    >
      {/* Icon Container */}
      <div className='flex size-[46px] shrink-0 items-center justify-center rounded-[14px] bg-brand-800'>
        {getIcon()}
      </div>

      {/* Content Area */}
      <div className='flex min-w-0 flex-1 flex-col justify-center gap-1.5'>
        {/* Top Row: Name + Status Text */}
        <div className='flex w-full items-center justify-between'>
          <span className='truncate pr-4 font-sans text-16 font-medium leading-20 text-dark-300'>
            {file.name ?? file.filename ?? 'File'}
          </span>

          {/* Status Text Actions */}
          {file.status === 'uploading' && (
            <button
              onClick={() => onCancel(file.id)}
              className='font-sans text-[15px] font-bold leading-nav text-brand-600 underline decoration-solid hover:opacity-80'
            >
              Cancel
            </button>
          )}

          {file.status === 'completed' && (
            <span className='font-sans text-[15px] font-normal leading-nav text-brand-800'>
              Completed
            </span>
          )}

          {file.status === 'error' && (
            <span className='font-sans text-[15px] font-normal leading-nav text-red-550'>
              Failed
            </span>
          )}
        </div>

        {/* Progress Bar */}
        <div className='h-[4px] w-full overflow-hidden rounded-20 bg-gray-200'>
          <div
            className={clsx(
              'h-full rounded-20 transition-all duration-300',
              file.status === 'uploading' && 'bg-brand-300',
              file.status === 'completed' && 'bg-accent-300',
              file.status === 'error' && 'bg-red-150' // Background for failed bar per specs
            )}
            style={{ width: file.status === 'error' ? '100%' : `${file.progress}%` }}
          />
        </div>

        {/* Bottom Row: Size + Screenshot Toggle */}
        <div className='flex items-center justify-between'>
          <span className='font-sans text-12 leading-[100%] text-gray-450'>
            {formatSize(file.size)}
          </span>

          {showScreenshotToggle && (
            <label
              className='flex cursor-pointer items-center gap-1.5'
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type='checkbox'
                checked={file.contains_screenshots ?? false}
                onChange={(e) => onToggleScreenshots(file.id, e.target.checked)}
                className='size-3.5 rounded border-brand-300 text-brand-600 focus:ring-1 focus:ring-brand-500'
              />
              <span className='font-sans text-11 font-medium text-brand-600'>Contains images</span>
            </label>
          )}
        </div>
      </div>

      {/* Right Action Buttons (Outside main flex column for Completed/Failed) */}
      {file.status === 'completed' && (
        <button
          onClick={() => onDelete(file.id)}
          className='flex size-8 shrink-0 items-center justify-center rounded-full bg-red-100 transition-colors hover:bg-red-200'
          aria-label='Delete file'
        >
          <XMarkIcon className='size-5 text-red-500' strokeWidth={2} />
        </button>
      )}

      {file.status === 'error' && (
        <button
          onClick={() => onRetry(file.id)}
          className='flex size-8 shrink-0 items-center justify-center rounded-full bg-gray-200 transition-colors hover:bg-gray-300'
          aria-label='Retry upload'
        >
          <ArrowPathIcon className='size-5 text-gray-600' strokeWidth={2} />
        </button>
      )}
    </div>
  );
};
